import { supabase } from './supabaseClient';

/**
 * Fetch all active non-payment orders
 * @returns {Promise<Array>} Array of non-payment order objects
 */
export const fetchNonPaymentOrders = async () => {
  try {
    const { data, error } = await supabase
      .from('non_payment_orders')
      .select(`
        id,
        order_number,
        provider_id,
        reason,
        notes,
        created_at,
        created_by,
        is_active,
        providers:provider_id (
          id,
          name
        )
      `)
      .eq('is_active', true)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching non-payment orders:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Error in fetchNonPaymentOrders:', error);
    throw error;
  }
};

/**
 * Fetch non-payment orders as a Set for fast lookup during calculations
 * @returns {Promise<Set>} Set of uppercase order numbers
 */
export const fetchNonPaymentOrderSet = async () => {
  try {
    const orders = await fetchNonPaymentOrders();
    return new Set(orders.map(o => o.order_number.toUpperCase().trim()));
  } catch (error) {
    console.error('Error in fetchNonPaymentOrderSet:', error);
    // Return empty set on error to avoid breaking calculations
    return new Set();
  }
};

/**
 * Fetch non-payment orders as a Map for lookup with details (including reason)
 * @returns {Promise<Map>} Map of uppercase order numbers to order details
 */
export const fetchNonPaymentOrderMap = async () => {
  try {
    const orders = await fetchNonPaymentOrders();
    const orderMap = new Map();
    orders.forEach(order => {
      orderMap.set(order.order_number.toUpperCase().trim(), {
        id: order.id,
        orderNumber: order.order_number,
        reason: order.reason,
        notes: order.notes,
        providerName: order.providers?.name || null
      });
    });
    return orderMap;
  } catch (error) {
    console.error('Error in fetchNonPaymentOrderMap:', error);
    // Return empty map on error to avoid breaking calculations
    return new Map();
  }
};

/**
 * Get non-payment order details if order is in the non-payment list
 * @param {string} orderNumber - The order number to check
 * @param {Map} nonPaymentMap - Map of non-payment order details
 * @returns {Object|null} Order details if found, null otherwise
 */
export const getNonPaymentOrderDetails = (orderNumber, nonPaymentMap) => {
  if (!orderNumber || !nonPaymentMap) return null;
  return nonPaymentMap.get(orderNumber.toUpperCase().trim()) || null;
};

/**
 * Check if an order number is in the non-payment list
 * @param {string} orderNumber - The order number to check
 * @param {Set} nonPaymentSet - Set of non-payment order numbers (uppercase)
 * @returns {boolean} True if order should not be paid
 */
export const isNonPaymentOrder = (orderNumber, nonPaymentSet) => {
  if (!orderNumber || !nonPaymentSet) return false;
  return nonPaymentSet.has(orderNumber.toUpperCase().trim());
};

/**
 * Add a new non-payment order
 * @param {Object} orderData - Order data with order_number, provider_id (optional), reason, notes (optional)
 * @returns {Promise<Object>} Created order object
 */
export const addNonPaymentOrder = async (orderData) => {
  try {
    const { data, error } = await supabase
      .from('non_payment_orders')
      .insert([{
        order_number: orderData.order_number.trim(),
        provider_id: orderData.provider_id || null,
        reason: orderData.reason,
        notes: orderData.notes || null,
        is_active: true
      }])
      .select(`
        id,
        order_number,
        provider_id,
        reason,
        notes,
        created_at,
        created_by,
        is_active,
        providers:provider_id (
          id,
          name
        )
      `)
      .single();

    if (error) {
      console.error('Error adding non-payment order:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in addNonPaymentOrder:', error);
    throw error;
  }
};

/**
 * Update an existing non-payment order
 * @param {string} id - Order ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Updated order object
 */
export const updateNonPaymentOrder = async (id, updates) => {
  try {
    const updateData = {};
    if (updates.order_number !== undefined) updateData.order_number = updates.order_number.trim();
    if (updates.provider_id !== undefined) updateData.provider_id = updates.provider_id || null;
    if (updates.reason !== undefined) updateData.reason = updates.reason;
    if (updates.notes !== undefined) updateData.notes = updates.notes || null;

    const { data, error } = await supabase
      .from('non_payment_orders')
      .update(updateData)
      .eq('id', id)
      .select(`
        id,
        order_number,
        provider_id,
        reason,
        notes,
        created_at,
        created_by,
        is_active,
        providers:provider_id (
          id,
          name
        )
      `)
      .single();

    if (error) {
      console.error('Error updating non-payment order:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in updateNonPaymentOrder:', error);
    throw error;
  }
};

/**
 * Delete (deactivate) a non-payment order
 * @param {string} id - Order ID
 * @returns {Promise<void>}
 */
export const deleteNonPaymentOrder = async (id) => {
  try {
    const { error } = await supabase
      .from('non_payment_orders')
      .update({ is_active: false })
      .eq('id', id);

    if (error) {
      console.error('Error deleting non-payment order:', error);
      throw error;
    }
  } catch (error) {
    console.error('Error in deleteNonPaymentOrder:', error);
    throw error;
  }
};

/**
 * Get predefined reason options for non-payment orders
 * @returns {Array} Array of reason strings
 */
export const getNonPaymentReasons = () => {
  return [
    'Incomplete Work',
    'Quality Issues',
    'Customer Complaint',
    'Wrong Location',
    'Duplicate Order',
    'Provider No-Show',
    'Other'
  ];
};

/**
 * Fetch active providers for dropdown selection
 * @returns {Promise<Array>} Array of provider objects with id and name
 */
export const fetchActiveProviders = async () => {
  try {
    const { data, error } = await supabase
      .from('providers')
      .select('id, name')
      .eq('status', 'active')
      .order('name');

    if (error) {
      console.error('Error fetching providers:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Error in fetchActiveProviders:', error);
    throw error;
  }
};
